/* eslint-disable import/no-cycle */
import { Grid, Stack, Button } from '@mui/material';
import { RideOfferType, TravelTypeTabs } from 'remote/global';
import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RideDetailsSchemaType } from '../types';
import VehicleCityHireRideStepForm from './steps/VehicleCityHireRideStepForm';
import VehicleMultiStopStepForm from './steps/VehicleMultiStopRideStepForm';
import VehicleOneWayRideStepForm from './steps/VehicleOneWayRideStepForm';
import VehicleRoundTripRideStepForm from './steps/VehicleRoundTripRideStepForm';

export function RideOffersFormSubmitButton({
  isSubmitting,
  isValidating,
  isLoading,
}: {
  isSubmitting: boolean;
  isValidating: boolean;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Stack width='100%' direction='row' justifyContent='flex-end' spacing={1} my='2rem'>
      <Button
        sx={{ marginTop: '24px', marginBottom: '16px', maxWidth: 'max-content' }}
        fullWidth
        type='submit'
        variant='contained'
        color='primary'
        disableElevation
        disabled={isSubmitting || isValidating || isLoading}
      >
        {t('continue')}
      </Button>
    </Stack>
  );
}

interface TravelTypeTabsContainerProps {
  setFormResult: React.Dispatch<React.SetStateAction<Partial<RideDetailsSchemaType> | undefined>>;
  getValues: () => Partial<RideDetailsSchemaType>;
  type: RideOfferType;
}

export function TravelTypeTabsContainer({
  setFormResult,
  getValues,
  type,
}: TravelTypeTabsContainerProps) {
  const tInstance = useTranslation();
  const { t } = tInstance;

  return (
    <Grid item container>
      <TravelTypeTabs
        label={t('FormFields.rideType.label') as string}
        value={type}
        showTooltip={false}
        options={[
          RideOfferType.ONE_WAY,
          RideOfferType.ROUND_TRIP,
          RideOfferType.MULTI_STOP,
          RideOfferType.CITY_HIRE,
        ]}
        handleChange={(value) => {
          setFormResult({
            ...getValues(),
            type: value as RideOfferType,
          }); // Sets the form defaultValues as the current forms values
        }}
      />
    </Grid>
  );
}

function CreateRideOffersForm({
  onValid,
  defaultValues,
}: {
  onValid: SubmitHandler<RideDetailsSchemaType>;
  defaultValues: Partial<RideDetailsSchemaType> | undefined;
}) {
  const [formResult, setFormResult] = useState<Partial<RideDetailsSchemaType> | undefined>(
    defaultValues
  );
  const type = formResult?.type ?? RideOfferType.ROUND_TRIP;

  return (
    <Grid container item gap={2}>
      <Grid item xs={12}>
        {type === RideOfferType.ROUND_TRIP && (
          <VehicleRoundTripRideStepForm
            defaultValues={formResult}
            onValid={onValid}
            setFormResult={setFormResult}
          />
        )}
        {type === RideOfferType.MULTI_STOP && (
          <VehicleMultiStopStepForm
            defaultValues={formResult}
            onValid={onValid}
            setFormResult={setFormResult}
          />
        )}
        {type === RideOfferType.ONE_WAY && (
          <VehicleOneWayRideStepForm
            defaultValues={formResult}
            onValid={onValid}
            setFormResult={setFormResult}
          />
        )}
        {type === RideOfferType.CITY_HIRE && (
          <VehicleCityHireRideStepForm
            defaultValues={formResult}
            onValid={onValid}
            setFormResult={setFormResult}
          />
        )}
      </Grid>
    </Grid>
  );
}

export default CreateRideOffersForm;
